import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Building2,
  Settings,
  Users,
  FileText,
  ChevronLeft,
  ChevronRight,
  Shield,
  Sparkles,
  ShieldCheck,
  UserCog,
  KeyRound,
  Bug,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface GodModeSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const menuItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/godmode/dashboard',
  },
  {
    title: 'Tenants',
    icon: Building2,
    path: '/godmode/tenants',
  },
  {
    title: 'Usuários God Mode',
    icon: ShieldCheck,
    path: '/godmode/users',
  },
  {
    title: 'Usuários de Teste',
    icon: UserCog,
    path: '/godmode/test-users',
  },
  {
    title: 'Créditos de IA',
    icon: Sparkles,
    path: '/godmode/ai-credits',
  },
  {
    title: 'Chaves Seguras',
    icon: KeyRound,
    path: '/godmode/secrets',
  },
  {
    title: 'Configurações Globais',
    icon: Settings,
    path: '/godmode/settings',
  },
  {
    title: 'Bugs Reportados',
    icon: Bug,
    path: '/godmode/bug-reports',
  },
  {
    title: 'Logs de Auditoria',
    icon: FileText,
    path: '/godmode/audit',
  },
];

export const GodModeSidebar: React.FC<GodModeSidebarProps> = ({ collapsed, onToggle }) => {
  const location = useLocation();

  return (
    <aside className={cn(
      "fixed left-0 top-0 h-full bg-slate-900 border-r border-slate-800 transition-all duration-300 z-40",
      collapsed ? "w-16" : "w-64"
    )}>
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Shield className="w-8 h-8 text-cyan-500" />
            <span className="font-bold text-lg text-cyan-400">God Mode</span>
          </div>
        )}
        {collapsed && (
          <Shield className="w-8 h-8 text-cyan-500 mx-auto" />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4">
        <ul className="space-y-1 px-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                    "hover:bg-slate-800/50",
                    isActive && "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20",
                    !isActive && "text-slate-400 hover:text-slate-200"
                  )}
                >
                  <item.icon className={cn(
                    "w-5 h-5 flex-shrink-0",
                    isActive ? "text-cyan-400" : "text-slate-500"
                  )} />
                  {!collapsed && (
                    <span className="font-medium">{item.title}</span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Collapse button */}
      <div className="absolute bottom-4 left-0 right-0 px-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="w-full justify-center text-slate-400 hover:text-slate-200 hover:bg-slate-800"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <>
              <ChevronLeft className="w-4 h-4 mr-2" />
              <span>Recolher</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
};
